/**
 * Status pages shown after PayGlocal redirects the customer back. The webhook
 * is the source of truth for the actual order status — these pages just poll
 * /api/payments/payglocal/status/:order_id (via gid) to give the customer
 * immediate feedback.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export function PayGlocalSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("orderId") || params.get("order_id");
  const pgLink = params.get("pg_link");
  const { clearCart } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | pending | failed
  const [pgStatus, setPgStatus] = useState(null);

  // PayGlocal echoes back either ?pg_link=<token> (legacy, if URL whitelist
  // accepted the query string) or just ?orderId=gl_o-… for both PG link and
  // regular order flows. Resolve the PG link via its gid before falling back
  // to the standard order-status poller so customers returning from a PG
  // Link Generator session land on /pl/<token>/status with the receipt CTA.
  useEffect(() => {
    if (pgLink) { nav(`/pl/${pgLink}/status`, { replace: true }); return; }
    if (!orderId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/pg/resolve-by-gid`, { params: { gid: orderId } });
        if (alive && data?.token) { nav(`/pl/${data.token}/status`, { replace: true }); return; }
      } catch { /* not a PG link — keep the regular order flow */ }
      try {
        const { data } = await api.get(`/sm888/resolve-by-gid`, { params: { gid: orderId } });
        if (alive && data?.order_id) nav(`/SM888/status/${data.order_id}`, { replace: true });
      } catch { /* not an SM888 order either */ }
    })();
    return () => { alive = false; };
  }, [pgLink, orderId, nav]);

  useEffect(() => {
    let alive = true;
    let attempts = 0;
    const poll = async () => {
      if (!orderId) { setStatus("failed"); return; }
      try {
        const { data } = await api.get(`/payments/payglocal/status/${orderId}`);
        if (!alive) return;
        setPgStatus(data.payglocal_status);
        if (data.status === "paid") { setStatus("paid"); clearCart(); return; }
        if (["AUTHORIZED","SENT_FOR_CAPTURE"].includes((data.payglocal_status||"").toUpperCase())) {
          setStatus("paid"); clearCart(); return;
        }
        if (["ISSUER_DECLINE","GENERAL_DECLINE","CUSTOMER_CANCELLED","ABANDONED","REQUEST_ERROR","FAILED"].includes((data.payglocal_status||"").toUpperCase())) {
          setStatus("failed"); return;
        }
        attempts++;
        if (attempts < 8) setTimeout(poll, 2500);
        else setStatus("pending");
      } catch {
        attempts++;
        if (attempts < 8) setTimeout(poll, 2500);
        else setStatus("failed");
      }
    };
    poll();
    return () => { alive = false; };
  }, [orderId, clearCart]);

  return (
    <div className="section max-w-xl text-center" data-testid="payglocal-success-page">
      {status === "checking" && (
        <>
          <Loader2 size={42} className="mx-auto text-cyan-300 animate-spin mb-4"/>
          <h1 className="text-3xl font-bold text-white" style={{fontFamily:"Outfit"}}>Confirming your payment…</h1>
          <p className="text-slate-400 mt-2">We're reconciling with PayGlocal — this usually takes 2-5 seconds.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 size={48} className="mx-auto text-emerald-400 mb-4"/>
          <h1 className="text-3xl font-bold text-white" style={{fontFamily:"Outfit"}}>Payment received</h1>
          <p className="text-slate-400 mt-2">Thank you! Your order is now queued for activation — you'll get a confirmation email with your invoice shortly.</p>
          <button onClick={()=>nav("/dashboard/orders")} className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="success-go-orders">
            Go to my orders <ArrowRight size={14}/>
          </button>
        </>
      )}
      {status === "pending" && (
        <>
          <Loader2 size={42} className="mx-auto text-amber-300 animate-spin mb-4"/>
          <h1 className="text-3xl font-bold text-white" style={{fontFamily:"Outfit"}}>Payment is pending</h1>
          <p className="text-slate-400 mt-2">We haven't received the final confirmation from PayGlocal yet ({pgStatus || "unknown"}). You'll get an email as soon as it's settled. You can refresh this page or check your dashboard.</p>
          <button onClick={()=>nav("/dashboard/orders")} className="btn-ghost mt-6 inline-flex items-center gap-2" data-testid="pending-go-orders">
            Open my dashboard
          </button>
        </>
      )}
      {status === "failed" && (
        <>
          <XCircle size={48} className="mx-auto text-rose-400 mb-4"/>
          <h1 className="text-3xl font-bold text-white" style={{fontFamily:"Outfit"}}>Payment was not completed</h1>
          <p className="text-slate-400 mt-2">{pgStatus ? `PayGlocal returned: ${pgStatus}` : "We couldn't confirm your payment."}</p>
          <button onClick={()=>nav("/checkout")} className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="failed-retry">
            Try again
          </button>
        </>
      )}
    </div>
  );
}

export function PayGlocalFailed() {
  const nav = useNavigate();
  return (
    <div className="section max-w-xl text-center" data-testid="payglocal-failed-page">
      <XCircle size={48} className="mx-auto text-rose-400 mb-4"/>
      <h1 className="text-3xl font-bold text-white" style={{fontFamily:"Outfit"}}>Payment failed</h1>
      <p className="text-slate-400 mt-2">Your card was not charged. You can try again from checkout — your items are still in your cart.</p>
      <button onClick={()=>nav("/checkout")} className="btn-primary mt-6">Back to checkout</button>
    </div>
  );
}

export default PayGlocalSuccess;
